import React, { useState, useEffect } from "react";
import {
  Clock,
  Sparkles,
  Bell,
  Rocket,
  CheckCircle,
  Circle,
  Code,
  Palette,
  Bug,
  Users,
  Zap,
} from "lucide-react";

const ComingSoonCard = ({
  stage = 3,
  featureName = "Next-Gen Analytics Dashboard",
  description = "Revolutionizing data visualization with AI-powered insights and real-time collaboration features",
}) => {
  const [animatedStage, setAnimatedStage] = useState(0);
  const [pulseIndex, setPulseIndex] = useState(0);

  const developmentStages = [
    {
      id: 1,
      name: "Feature Validation",
      icon: CheckCircle,
      color: "from-green-500 to-emerald-600",
      description: "Market research & user feedback analysis",
    },
    {
      id: 2,
      name: "System Design",
      icon: Code,
      color: "from-blue-500 to-indigo-600",
      description: "Architecture planning & technical specifications",
    },
    {
      id: 3,
      name: "UI Design Standards",
      icon: Palette,
      color: "from-purple-500 to-violet-600",
      description: "User interface mockups & design system creation",
    },
    {
      id: 4,
      name: "Development",
      icon: Code,
      color: "from-orange-500 to-red-600",
      description: "Core functionality implementation",
    },
    {
      id: 5,
      name: "End-to-End Testing",
      icon: CheckCircle,
      color: "from-teal-500 to-cyan-600",
      description: "Comprehensive testing across all systems",
    },
    {
      id: 6,
      name: "Bug Fixes",
      icon: Bug,
      color: "from-yellow-500 to-amber-600",
      description: "Resolving issues & performance optimization",
    },
    {
      id: 7,
      name: "User Acceptance Testing",
      icon: Users,
      color: "from-pink-500 to-rose-600",
      description: "Beta testing with real users",
    },
    {
      id: 8,
      name: "Launch Preparations",
      icon: Zap,
      color: "from-indigo-500 to-purple-600",
      description: "Final polish & deployment readiness",
    },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      if (animatedStage < stage) {
        setAnimatedStage(animatedStage + 1);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [animatedStage, stage]);

  useEffect(() => {
    const pulseTimer = setInterval(() => {
      setPulseIndex((prev) => (prev + 1) % developmentStages.length);
    }, 2000);

    return () => clearInterval(pulseTimer);
  }, []);

  const progressPercentage = (stage / developmentStages.length) * 100;

  return (
    <div
      style={{
        scrollbarWidth: "none",
      }}
      className="bg-gradient-to-br w-full from-indigo-50 via-white to-purple-50 rounded-3xl p-8 relative overflow-hidden transition-shadow overflow-y-scroll duration-500"
    >
      {/* Enhanced background decorative elements */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-indigo-200 to-transparent rounded-full -translate-y-20 translate-x-20 opacity-30 animate-pulse"></div>
      <div
        className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-purple-200 to-transparent rounded-full translate-y-16 -translate-x-16 opacity-30 animate-pulse"
        style={{ animationDelay: "1s" }}
      ></div>
      <div
        className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full -translate-x-32 -translate-y-32 opacity-20 animate-spin"
        style={{ animationDuration: "20s" }}
      ></div>

      <div className="relative z-10 space-y-8">
        {/* Enhanced icon section */}
        <div className="flex justify-center">
          <div className="relative group">
            <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl transform hover:scale-110 transition-transform duration-300">
              <Rocket className="w-10 h-10 text-white animate-bounce" />
            </div>
            <div
              className="absolute -top-2 -right-2 animate-spin"
              style={{ animationDuration: "3s" }}
            >
              <Sparkles className="w-8 h-8 text-yellow-400 drop-shadow-lg" />
            </div>
            <div className="absolute -bottom-1 -left-1">
              <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center animate-ping">
                <div className="w-3 h-3 bg-green-600 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature info section */}
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            {featureName}
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
            {description}
          </p>
        </div>

        {/* Interactive development stages */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-center text-gray-700 flex items-center justify-center space-x-2">
            <Code className="w-5 h-5 text-indigo-500" />
            <span>Development Progress</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {developmentStages.map((stageItem, index) => {
              const StageIcon = stageItem.icon;
              const isCompleted = index + 1 <= animatedStage;
              const isCurrent = index + 1 === stage;
              const shouldPulse = pulseIndex === index && !isCompleted;

              return (
                <div
                  key={stageItem.id}
                  className={`flex items-center space-x-3 p-3 rounded-xl transition-all duration-500 hover:scale-105 ${
                    isCompleted
                      ? "bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200"
                      : isCurrent
                      ? "bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 shadow-md"
                      : "bg-gray-50 border border-gray-200"
                  } ${shouldPulse ? "animate-pulse" : ""}`}
                  style={{
                    transform: `translateX(${isCompleted ? "0" : index * 2}px)`,
                    transition: "transform 0.5s ease-out",
                  }}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                      isCompleted
                        ? `bg-gradient-to-r ${stageItem.color} shadow-lg`
                        : isCurrent
                        ? "bg-blue-500 shadow-md animate-pulse"
                        : "bg-gray-300"
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-5 h-5 text-white" />
                    ) : (
                      <StageIcon
                        className={`w-4 h-4 ${
                          isCurrent ? "text-white" : "text-gray-500"
                        }`}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <p
                      className={`font-medium text-sm ${
                        isCompleted
                          ? "text-green-700"
                          : isCurrent
                          ? "text-blue-700"
                          : "text-gray-500"
                      }`}
                    >
                      {stageItem.name}
                    </p>
                    <p
                      className={`text-xs ${
                        isCompleted
                          ? "text-green-600"
                          : isCurrent
                          ? "text-blue-600"
                          : "text-gray-400"
                      }`}
                    >
                      {stageItem.description}
                    </p>
                  </div>
                  {isCompleted && (
                    <div className="animate-bounce">
                      <Sparkles className="w-4 h-4 text-green-500" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Enhanced progress bar */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500 font-medium">
              Overall Progress
            </p>
            <p className="text-sm font-bold text-indigo-600">
              {Math.round(progressPercentage)}%
            </p>
          </div>
          <div className="relative w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
            <div
              className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 h-3 rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
              style={{ width: `${progressPercentage}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
            </div>
          </div>
          <div className="flex justify-between text-xs text-gray-400">
            <span>Started</span>
            <span className="font-semibold text-indigo-600">
              Stage {stage} of {developmentStages.length}
            </span>
            <span>Launch Ready</span>
          </div>
        </div>

        {/* Interactive CTA section */}
        {/* <div className="pt-6 text-center space-y-4">
            <button className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:shadow-2xl hover:scale-105 transform transition-all duration-300 flex items-center space-x-3 mx-auto group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <Bell className="w-5 h-5 animate-swing relative z-10" />
              <span className="relative z-10">Get Notified at Launch</span>
              <Sparkles className="w-5 h-5 animate-pulse relative z-10" />
            </button>
            
            <p className="text-sm text-gray-500">
              Join <span className="font-bold text-indigo-600 animate-pulse">2,847</span> others waiting for this release
            </p>
          </div> */}

        {/* Timeline section with dynamic messaging */}
        <div className="pt-4 text-center">
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-indigo-100 to-purple-100 px-4 py-2 rounded-full">
            <Clock className="w-4 h-4 text-indigo-500 animate-pulse" />
            <p className="text-sm">
              <span className="text-gray-600">Estimated completion: </span>
              <span className="font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                {stage >= 8
                  ? "Ready to Launch!"
                  : stage >= 6
                  ? "2-3 weeks"
                  : stage >= 4
                  ? "4-6 weeks"
                  : "6-8 weeks"}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Floating particles */}
      <div
        className="absolute top-1/4 left-1/4 w-2 h-2 bg-indigo-400 rounded-full animate-ping opacity-60"
        style={{ animationDelay: "2s" }}
      ></div>
      <div
        className="absolute top-3/4 right-1/4 w-1 h-1 bg-purple-400 rounded-full animate-ping opacity-60"
        style={{ animationDelay: "4s" }}
      ></div>
      <div
        className="absolute top-1/2 right-1/3 w-1.5 h-1.5 bg-pink-400 rounded-full animate-ping opacity-60"
        style={{ animationDelay: "6s" }}
      ></div>
    </div>
  );
};

export default ComingSoonCard;
